import React from 'react';
import { Controller, type Path, useFormContext } from 'react-hook-form';
import { BadgeSelect } from '@/app/(main)/clients/create/components/BadgeSelect';
import RadioButton from '@/app/components/buttons/RadioButton';
import { TextArea } from '@/app/components/form/TextArea';
import {
  SEAT_PREFERENCES,
  CLASS_PREFERENCE_OPTIONS,
  type ClassPreference,
  type CreateClientFormData,
  type EditClientResponse,
} from '@/types/clientsApiTypes';
import { RELATED_DATA_KEYS, type RelatedDataKeys } from '../../hooks/useUpdateTravelPreferencesWithRelatedData/types';

interface FlightPreferencesProps {
  onUpdateTravelPreferencesWithRelatedData?: (fieldKey: RelatedDataKeys, fieldName: string, fieldValue: unknown, fullPath: string) => Promise<EditClientResponse | undefined>;
}

export const FlightPreferences = ({ onUpdateTravelPreferencesWithRelatedData }: FlightPreferencesProps) => {
  const basePath = 'primary_passenger.preference_data';
  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<CreateClientFormData>();

  const onUpdateFlightPreference = (field: Path<CreateClientFormData>, fieldName: string, value: string | ClassPreference[]) => {
    setValue(field, value);
    onUpdateTravelPreferencesWithRelatedData?.(RELATED_DATA_KEYS.fly, fieldName, value, `${basePath}.${field}`);
  };

  const seatPreference = watch(`${basePath}.fly.seat`);
  const flyPrivateAviation = watch(`${basePath}.fly.fly_private_aviation`);

  const isPrivate = flyPrivateAviation === 'yes';
  const isCommercial = flyPrivateAviation === 'no';

  return (
    <div>
      <div className="flex items-center gap-6 mb-4 mt-4">
        <RadioButton
          label="Commercial"
          checked={flyPrivateAviation === 'no'}
          onChange={() => {
            onUpdateFlightPreference(`${basePath}.fly.fly_private_aviation`, 'fly_private_aviation', 'no');
          }}
        />
        <RadioButton
          label="Private aviation"
          checked={flyPrivateAviation === 'yes'}
          onChange={() => {
            onUpdateFlightPreference(`${basePath}.fly.fly_private_aviation`, 'fly_private_aviation', 'yes');
          }}
        />
      </div>

      {isPrivate && (
        <TextArea
          {...register(`${basePath}.fly.preferred_private_aviation_company`)}
          disableResize
          label="Preferred private aviation company"
          placeholder="Please provide any relevant information we should know about your private aviation preferences."
          rows={4}
          errors={errors}
          onBlur={(e) => {
            onUpdateFlightPreference(`${basePath}.fly.preferred_private_aviation_company`, 'preferred_private_aviation_company', e.target.value);
          }}
        />
      )}

      {isCommercial && (
        <>
          <p className="text-body-small text-blue-900 mt-4 mb-3">Seat Preference</p>
          <div className="flex items-center gap-6 mb-4">
            <RadioButton
              label="Window"
              checked={seatPreference === SEAT_PREFERENCES.window}
              onChange={() => {
                onUpdateFlightPreference(`${basePath}.fly.seat`, 'seat', SEAT_PREFERENCES.window);
              }}
            />
            <RadioButton
              label="Aisle"
              checked={seatPreference === SEAT_PREFERENCES.aisle}
              onChange={() => {
                onUpdateFlightPreference(`${basePath}.fly.seat`, 'seat', SEAT_PREFERENCES.aisle);
              }}
            />
          </div>
          <div className="w-[60%]">
            <Controller
              name={`${basePath}.fly.class_preference`}
              control={control}
              render={({ field }) => (
                <BadgeSelect<ClassPreference>
                  label="Class Preference"
                  hint="Select all that apply"
                  options={CLASS_PREFERENCE_OPTIONS}
                  value={(field.value as ClassPreference[]) ?? []}
                  onChange={(value) => {
                    onUpdateFlightPreference(`${basePath}.fly.class_preference`, 'class_preference', value);
                   field.onChange(value);
                  }}
                />
              )}
            />
          </div>

        </>
      )}
    </div>
  );
};
